import { Injectable, Injector, OnDestroy, inject } from "@angular/core";
import { NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import _, { isNil } from "lodash";
import {
    BehaviorSubject,
    Observable,
    Subject,
    asapScheduler,
    merge,
    of
} from "rxjs";
import {
    distinctUntilChanged,
    filter,
    map,
    scan,
    share,
    shareReplay,
    switchMap,
    take,
    takeUntil,
    tap
} from "rxjs/operators";
import { EntityType, VirkeskopAffarsformer, VirkeskopBlobStorageTyp } from "~app/common/models/constants";
import { IVirkeskopKontraktEntityDTO } from "~app/common/models/entity-models";
import { Period } from "~app/common/models/period";
import { BasicCommand } from "~app/common/services/cqs/BasicCommand";
import { CommandManager } from "~app/common/services/cqs/commandManager";
import { EventHubService } from "~app/common/services/eventhub.service";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IKartgeometri } from "~app/features/map/popups/kartgeometri-popup/kartgeometri.model";
import { ChangeKontraktAffarsformCommand } from "~app/features/virkeskop/commands/kontrakt/ChangeKontraktAffarsformCommand";
import { ChangeKontraktTypCommand } from "~app/features/virkeskop/commands/kontrakt/ChangeKontraktTypCommand";
import { CreateGrotkontraktCommand } from "~app/features/virkeskop/commands/kontrakt/CreateGrotkontraktCommand";
import { CreateKontraktCommand } from "~app/features/virkeskop/commands/kontrakt/CreateKontraktCommand";
import { UpdateGrotkontraktCommand } from "~app/features/virkeskop/commands/kontrakt/UpdateGrotkontraktCommand";
import { UpdateKontraktCommand } from "~app/features/virkeskop/commands/kontrakt/UpdateKontraktCommand";
import { AtgardKedjaCopyGeometryDialogComponent } from "../atgarder/atgard-kedja-copy-geometry-dialog/atgard-kedja-copy-geometry-dialog.component";
import { AtgardKedjaHanteringsDialogComponent } from "../atgarder/atgard-kedja-hanterings-dialog/atgard-kedja-hanterings-dialog.component";
import { ChangeAtgardTypCommand } from "../commands/atgard/ChangeAtgardTypCommand";
import { RemoveAtgardCommand } from "../commands/atgard/RemoveAtgardCommand";
import { UpdateKontraktCertifikatCommand } from "../commands/certifikat/UpdateKontraktCertifikatCommand";
import { UpdateKontraktUtskriftCommand } from "../commands/utskrift/UpdateKontraktUtskriftCommand";
import { VirkeskopDokumentInfoDTO } from "../dto/dokument/VirkeskopDokumentInfoDTO";
import { IVKKontraktKopplingarDTO } from "../dto/kontrakt/kontrakt-kopplingar-dto";
import { RitunderlagMapDialogComponent } from "../karta/ritunderlag-map-dialog/ritunderlag-map-dialog.component";
import {
    Atgard,
    AtgardEntity,
    IAtgardstyper,
    SkogsvardEntity,
    VkAtgardEntity
} from "../models/atgard-models";
import { VKAtgardKedjaKopplingCopyGeometryDTO } from "../models/Atgardskedjor/atgardskedjor-models";
import { IAtgardNettoareal } from "../models/avverkningsanmalan-models";
import { KontraktCertifikatEditDTO } from "../models/certifiering-models";
import { Kontrakt, VirkeskopKontraktstyp } from "../models/kontrakt-models";
import { AtgardProduktionsdatum } from "../models/leveransvirkesplan-models";
import { CreateSkogsvard } from "../models/skogsvard/createskogsvard-models";
import { KontraktUtskriftValues } from "../models/utskrift/utskriftValues-models";
import { Vardeberakning } from "../models/vardeberakning/vardeberakning-models";
import { SkogsvardCreateDialogComponent } from "../skogsvard/skogsvard-create-dialog/skogsvard-create-dialog.component";
import { SkogsvardCreateOchSokDialogComponent } from "../skogsvard/skogsvard-create-och-sok-dialog/skogsvard-create-och-sok-dialog.component";
import { TabStateHelper } from "../utils/tab-state-helper";
import { AtgardKedjaMediatorService } from "./atgardkedja-mediator.service";
import { AvtalMediatorService } from "./avtal-mediator.service";
import { AvtalService } from "./avtal.service";
import { ForkalkylService } from "./forkalkyl.service";
import { KontraktCommandService } from "./kontrakt-command.service";
import { KontraktDokumentService } from "./kontrakt-dokument.service";
import { VirkeskopValidationService } from "./validation/virkeskop-validation.service";
import { VkKartaMediatorService } from "./vk-karta-mediator.service";

@Injectable()
export class KontraktMediatorService implements OnDestroy {

    anbudGiltigtChanged$ = new Subject<Period>();
    atgardForeslagetProduktionsdatumchanged$ = new Subject<AtgardProduktionsdatum>();
    atgardRemoved$ = new BehaviorSubject<Atgard>(null);
    atgardUpdate$ = new Subject<Atgard>();
    atgardsTyper$: Observable<IAtgardstyper>;
    kontraktGiltigtChanged$ = new Subject<Period>();
    kontraktKopplingar$: Observable<IVKKontraktKopplingarDTO>;
    kontraktNettoarealTotal$: Observable<IAtgardNettoareal[]>;
    kontraktstypChanged$ = new Subject<string>();
    loading$ = new Subject<boolean>();
    kontaktGeometrierToImport$ = new Subject<IKartgeometri[]>();
    ritunderlagRemoved$ = new BehaviorSubject<VirkeskopDokumentInfoDTO>(null);
    statusChanged$ = new Subject<string>();
    tabStateHelper = new TabStateHelper();
    updateNettoarealSrc = new Subject<IAtgardNettoareal>();
    vardeberakning$: Observable<Vardeberakning[]>;

    private readonly readonlySrc = new BehaviorSubject<boolean>(false);
    private readonly atgardAddedSrc = new Subject<VkAtgardEntity>();
    private readonly atgarderLoadedSrc = new BehaviorSubject<boolean>(false);
    private readonly atgarderPreloadSrc = new BehaviorSubject<VkAtgardEntity[]>([]);
    private readonly atgarderSrc = new BehaviorSubject<Atgard[]>([]);
    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly avtalService = inject(AvtalService);
    private readonly commandManager = inject(CommandManager);
    private readonly destroy$ = new Subject<void>();
    private readonly eventHubService = inject(EventHubService);
    private readonly forkalkylService = inject(ForkalkylService);
    private readonly kontraktDokumentService = inject(KontraktDokumentService);
    private readonly kontraktSrc = new BehaviorSubject<Kontrakt>(null);
    private readonly kontraktUpdateSrc = new Subject<Kontrakt>();
    private readonly localInjector = inject(Injector);
    private readonly modalService = inject(NgbModal);
    private readonly ritunderlagSrc = new BehaviorSubject<VirkeskopDokumentInfoDTO[]>([]);
    private readonly validationService = inject(VirkeskopValidationService);
    constructor() {
        this.vardeberakning$ = this.kontrakt$.pipe(
            map(kontrakt => kontrakt.id),
            filter(kontraktId => !isNil(kontraktId)),
            switchMap(kontrakdId => this.forkalkylService.vardeberakningDefault(kontrakdId)),
            shareReplay(1));

        const updateNettoareal$ = this.updateNettoarealSrc.pipe(
            share(),
            map(nettoareal => (arealer: IAtgardNettoareal[]) => {
                const index = arealer.findIndex(a => a.atgardGuid === nettoareal.atgardGuid);
                if (index > -1) {
                    return [
                        ...arealer.slice(0, index),
                        nettoareal,
                        ...arealer.slice(index + 1)
                    ];
                }
                return [
                    ...arealer,
                    nettoareal
                ];
            })
        );

        const synchronizeArealer$ = this.atgarderPreload$.pipe(
            share(),
            // eslint-disable-next-line sonarjs/no-nested-functions
            map(atgarder => (arealer: IAtgardNettoareal[]) => arealer.filter(a => atgarder.some(atg => atg.guid === a.atgardGuid)))
        );

        this.kontraktNettoarealTotal$ = merge(updateNettoareal$, synchronizeArealer$).pipe(
            scan((
                acc: IAtgardNettoareal[], mutatorFn: (arealer: IAtgardNettoareal[]) => IAtgardNettoareal[]) => mutatorFn(acc), []
            ),
            shareReplay(1)
        );

        this.atgardsTyper$ = merge(this.kontraktUpdate$, this.kontrakt$).pipe(
            filter(kontrakt =>
                ObjectUtils.hasValue(kontrakt) &&
                ObjectUtils.hasValue(kontrakt.kontraktTyp) &&
                ObjectUtils.hasValue(kontrakt.affarsform)),
            map(kontrakt => ({
                kontraktTyp: kontrakt.kontraktTyp,
                affarsform: kontrakt.affarsform
            })),
            distinctUntilChanged((a, b) => a.affarsform === b.affarsform && a.kontraktTyp === b.kontraktTyp),
            switchMap(kontrakt => this.avtalService.getAtgardstyper(kontrakt.kontraktTyp, kontrakt.affarsform)),
            shareReplay(1)
        );

        this.kontraktKopplingar$ = this.kontrakt$.pipe(
            filter(kontrakt => !isNil(kontrakt) && !isNil(kontrakt.id)),
            switchMap(kontrakt => this.avtalService.getKontraktKopplingar(kontrakt.id))
        );

        this.atgarder$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(atgardLista => {
            if (this.atgarderLoadedSrc.value !== true && atgardLista.length === this.atgarderPreload.length && atgardLista.length > 0) {
                this.atgarderLoadedSrc.next(true);
            }
        });
    }

    get atgardAdded$() {
        return this.atgardAddedSrc.asObservable();
    }

    get atgarder() {
        return this.atgarderSrc.value;
    }

    get atgarder$() {
        return this.atgarderSrc.asObservable();
    }

    get atgarderLoaded$() {
        return this.atgarderLoadedSrc.asObservable();
    }

    get atgarderPreload() {
        return this.atgarderPreloadSrc.value;
    }

    get atgarderPreload$() {
        return this.atgarderPreloadSrc.asObservable();
    }

    get isReadonly$(): Observable<boolean> {
        return this.readonlySrc.asObservable();
    }

    get kontrakt() {
        return this.kontraktSrc.value;
    }

    get kontrakt$() {
        return this.kontraktSrc.asObservable();
    }

    get kontraktUpdate$(): Observable<Kontrakt> {
        return this.kontraktUpdateSrc.asObservable();
    }

    get ritunderlag() {
        return this.ritunderlagSrc.value;
    }

    get ritunderlag$() {
        return this.ritunderlagSrc.asObservable();
    }

    private get atgardKedjaMediatorService() {
        return this.localInjector.get(AtgardKedjaMediatorService);
    }

    // circular dependency
    // https://stackoverflow.com/questions/46832072/how-to-solve-the-circular-dependency
    private get kontraktCommandService() {
        return this.localInjector.get(KontraktCommandService);
    }

    private get vkKartaMediatorService() {
        return this.localInjector.get(VkKartaMediatorService);
    }

    addAtgard(entity: AtgardEntity) {
        // vid ny atgard skapas en AtgardEntity, som sedan uppdaterar denna mediator via updateAtgard med full Atgardsobjekt
        const atgarder = _.clone(this.atgarderPreloadSrc.value);
        atgarder.push(entity);
        this.atgarderPreloadSrc.next(atgarder);
        this.atgardAddedSrc.next(entity);
    }

    addRitunderlag(dokument: VirkeskopDokumentInfoDTO) {
        // Måste skapa en ny lista här för att rask-list ska förstå att det skett en ändring
        const ritunderlag = [...this.ritunderlagSrc.value];

        const ifAlreadyExistPos = ritunderlag.findIndex(b => b.dokumentnyckel === dokument.dokumentnyckel);
        if (ifAlreadyExistPos > -1) {
            ritunderlag.splice(ifAlreadyExistPos, 1);
        }
        const convertedDate = new Date(dokument.skapadTidpunkt);
        ritunderlag.push({
            id: dokument.id,
            virkeskopskontraktId: dokument.virkeskopskontraktId,
            virkeskopsatgardId: dokument.virkeskopsatgardId,
            filnamn: dokument.filnamn,
            dokumentnyckel: dokument.dokumentnyckel,
            version: dokument.version,
            skapadTidpunkt: `${convertedDate.toLocaleDateString("sv-SE")} ${convertedDate.toLocaleTimeString("sv-SE")}`,
            dokumenttyp: dokument.dokumenttyp
        });

        this.ritunderlagSrc.next(ritunderlag);
    }

    createKontrakt(kontrakt: Kontrakt) {
        const command = new CreateKontraktCommand(kontrakt, this.avtalMediatorService.avtal.guid);
        this.kontraktCommandService.update(command);
    }

    getAtgard(id: number): Observable<Atgard> {
        return this.avtalService.getAtgard(id)
            .pipe(tap((a) => this.updateKontraktAtgard(a)));
    }

    getKontraktNettoareal(condition: (areal: IAtgardNettoareal) => boolean): Observable<number> {
        return this.kontraktNettoarealTotal$.pipe(
            map(arealer => arealer
                .filter(areal => condition(areal))
                .reduce((acc, curr) => acc + curr.nettoareal, 0))
        );
    }

    handleKontraktAffarsformChange(affarsform: string) {
        if (affarsform === VirkeskopAffarsformer.Leveransvirke) {
            const newAtgard: AtgardEntity = {
                id: null,
                name: VirkeskopAffarsformer.Leveransvirke,
                type: EntityType.virkeskopsAtgard,
                guid: GuidUtils.newGuid(),
                avverkningsTyp: {
                    kod: VirkeskopAffarsformer.Leveransvirke,
                    grupp: VirkeskopAffarsformer.Leveransvirke
                }
            };
            this.addAtgard(newAtgard);
        }
    }

    initKontrakt(entity: IVirkeskopKontraktEntityDTO) {
        if (entity.id) {
            this.loading$.next(true);
            this.reloadKontrakt(entity.id);
        } else { // nytt kontrakt
            let newKontrakt: Kontrakt;
            if (this.avtalMediatorService.kontraktCreated$.value?.guid === entity?.guid) {
                // hämta från avtal-mediator
                newKontrakt = this.avtalMediatorService.kontraktCreated$.value;
                this.avtalMediatorService.kontraktCreated$.next(null);
                asapScheduler.schedule(() => {
                    this.createKontrakt(this.kontrakt);
                    this.kontraktSrc.next(newKontrakt);
                    this.kontraktUpdateSrc.next(newKontrakt);
                });
            } else {
                // skapa blankt
                asapScheduler.schedule(() => {
                    if (entity.skogsvard) {
                        newKontrakt = this.avtalMediatorService.createKontrakt(this.avtalMediatorService.avtal, entity.guid, VirkeskopKontraktstyp.Skogsvard);
                    } else if (entity.skogsvardFPV) {
                        newKontrakt = this.avtalMediatorService.createKontrakt(this.avtalMediatorService.avtal, entity.guid, VirkeskopKontraktstyp.SkogsvardFPV);
                    } else {
                        newKontrakt = this.avtalMediatorService.createKontrakt(this.avtalMediatorService.avtal, entity.guid, VirkeskopKontraktstyp.Rundvirke);
                    }
                    this.kontraktSrc.next(newKontrakt);
                    this.kontraktUpdateSrc.next(newKontrakt);
                });
            }
        }
        this.loadAtgarder(entity.id);
        this.loadRitunderlag(entity.id);
    }

    isDirty(): boolean {
        return this.commandManager.isDirty() === true;
    }

    isGrotuttagforeslaget(): boolean {
        return this.atgarder?.some(x => x.grotuttagForeslaget === true);
    }

    loadAtgarder(kontraktId: number) {
        if (_.isNil(kontraktId)) {
            return;
        }

        const atgarderPreload$ = this.avtalService.getAtgarderForKontrakt(kontraktId);
        atgarderPreload$.subscribe(atgarder => {
            this.atgarderPreloadSrc.next(atgarder);
        });
    }

    addContractGeometries(geometrier: IKartgeometri[]) {
        this.kontaktGeometrierToImport$.next(geometrier);
    }

    loadRitunderlag(kontraktId: number) {
        if (_.isNil(kontraktId)) {
            return;
        }

        this.kontraktDokumentService.getDokumentForKontrakt(kontraktId).then(response => {
            if (response.length > 0) {
                const dokument: VirkeskopDokumentInfoDTO[] = [];
                response.filter(r => r.dokumenttyp === VirkeskopBlobStorageTyp.RitunderlagBilaga)
                    .forEach(d => {
                        const convertedDate = new Date(d.skapadTidpunkt);
                        dokument.push({
                            id: d.id,
                            virkeskopskontraktId: d.virkeskopskontraktId,
                            virkeskopsatgardId: d.virkeskopsatgardId,
                            filnamn: d.filnamn,
                            dokumentnyckel: d.dokumentnyckel,
                            version: d.version,
                            skapadTidpunkt: `${convertedDate.toLocaleDateString("sv-SE")} ${convertedDate.toLocaleTimeString("sv-SE")}`,
                            dokumenttyp: d.dokumenttyp
                        });
                    });
                this.ritunderlagSrc.next(dokument);
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.eventHubService.unregisterListener(`virkeskopskontrakt/${this.kontrakt.id}`, true);
    }

    openAtgardKedjeHandlerDialog = (kedjeGuid: string) => {
        // Här måste this.localInjector skickas in annars tas root injectorn och skickas in.
        const options = {
            keyboard: false,
            backdrop: "static",
            size: "xl",
            injector: this.localInjector
        } as NgbModalOptions;
        const modalRef = this.modalService.open(AtgardKedjaHanteringsDialogComponent, options);
        const componentInstance = (modalRef.componentInstance as AtgardKedjaHanteringsDialogComponent);
        componentInstance.init(kedjeGuid, this.kontrakt.id);

        (modalRef.result as Promise<CreateSkogsvard>).then(saved => {
            this.avtalMediatorService.reloadAvtal().subscribe();
            if (saved) {
                this.atgardKedjaMediatorService.reloadAvtalsKedjor();
            }
        });
    };

    openAtgardKedjeCopyGeometryDialog = (kedjeGuid: string) => {
        const options = {
            keyboard: false,
            backdrop: "static",
            size: "lg",
            injector: this.localInjector
        } as NgbModalOptions;

        const modalRef = this.modalService.open(AtgardKedjaCopyGeometryDialogComponent, options);
        const componentInstance = (modalRef.componentInstance as AtgardKedjaCopyGeometryDialogComponent);
        componentInstance.init(kedjeGuid, this.atgarderPreload, this.atgarder);

        (modalRef.result as Promise<VKAtgardKedjaKopplingCopyGeometryDTO>).then(copyDTO => {
            if (copyDTO) {
                const atgardSource = this.atgarder.find(x => x.guid === copyDTO.copyAtgardGuid);
                copyDTO.copyToAtgarderList.forEach(atgGuid => {
                    this.vkKartaMediatorService.copyAtgardToSkogsvardAtgardGeometri(this.kontrakt.guid, atgardSource.geometri, atgGuid);
                });
            }
        });
    };

    openOneOfSkogsvardCreateDialog(preSelectedAtgard?: string) {
        if (this.kontrakt.isAnySkogsvard) {
            this.openSkogsvardCreateOchSokDialog(preSelectedAtgard);
        } else {
            this.openSkogsvardCreateDialog(preSelectedAtgard);
        }
    }

    openRitunderlagDialog() {
        const options = {
            keyboard: false,
            backdrop: "static",
            size: "lg",
            injector: this.localInjector
        } as NgbModalOptions;
        this.modalService.open(RitunderlagMapDialogComponent, options);
    }

    openSkogsvardCreateDialog(preSelectedAtgard?: string) {
        const options = {
            keyboard: false,
            backdrop: "static",
            size: "lg",
            injector: this.localInjector
        } as NgbModalOptions;
        const modalRef = this.modalService.open(SkogsvardCreateDialogComponent, options);
        const componentInstance = (modalRef.componentInstance as SkogsvardCreateDialogComponent);
        componentInstance.atgarder = this.atgarder;
        componentInstance.kontraktList = [this.kontrakt];
        componentInstance.atgarderPreload = this.atgarderPreload;
        componentInstance.preSelectedAtgard = preSelectedAtgard;
        componentInstance.preSelectedKontraktGuid = this.kontrakt.guid;

        (modalRef.result as Promise<CreateSkogsvard>).then(skogsVard => {
            skogsVard.skogsvardRowTypes.forEach(row => {
                const atgardCopyGuid = Atgard.isPlantleverans(row.skogsvardGrupp) ? null : skogsVard.atgardCopyGuid;
                const skogsvardAtgard: SkogsvardEntity = {
                    id: null,
                    name: row.skogsvardGrupp,
                    type: EntityType.virkeskopsAtgardSkogsvard,
                    guid: GuidUtils.newGuid(),
                    grupp: row.skogsvardGrupp,
                    typ: row.skogsvardTyp,
                    lopnummerKedja: 0,
                    lopnummer: 0
                };
                if (skogsVard.atgardCopyGuid) {
                    skogsvardAtgard.createByCopy = {
                        copyFromAtgardGuid: atgardCopyGuid,
                        copyFromKontraktGuid: this.kontrakt.guid,
                        copyContractGeometries: skogsVard.copyContractGeometries
                    };
                }
                this.addAtgard(skogsvardAtgard);
            });

        },
        (error) => {
            //
        });
    }

    openSkogsvardCreateOchSokDialog(preSelectedAtgard?: string) {
        const options = {
            keyboard: false,
            backdrop: "static",
            size: "lg",
            injector: this.localInjector
        } as NgbModalOptions;
        const modalRef = this.modalService.open(SkogsvardCreateOchSokDialogComponent, options);
        const componentInstance = (modalRef.componentInstance as SkogsvardCreateOchSokDialogComponent);
        componentInstance.init(this.atgarder, this.kontrakt.kontraktTyp, preSelectedAtgard, this.kontrakt.guid, this.atgarderPreload);

        (modalRef.result as Promise<CreateSkogsvard>).then(skogsVard => {
            skogsVard.skogsvardRowTypes.forEach(row => {

                const atgardCopyGuid = Atgard.isPlantleverans(row.skogsvardGrupp) ? null : skogsVard.atgardCopyGuid;

                const skogsvardAtgard: SkogsvardEntity = {
                    id: null,
                    name: row.skogsvardGrupp,
                    type: EntityType.virkeskopsAtgardSkogsvard,
                    guid: GuidUtils.newGuid(),
                    grupp: row.skogsvardGrupp,
                    typ: row.skogsvardTyp,
                    lopnummerKedja: 0,
                    lopnummer: 0
                };
                if (skogsVard.atgardCopyGuid) {
                    skogsvardAtgard.createByCopy = {
                        copyFromAtgardGuid: atgardCopyGuid,
                        copyFromAtgardId: skogsVard.atgardCopyId,
                        copyFromKontraktGuid: skogsVard.kontraktCopyGuid,
                        copyContractGeometries: skogsVard.copyContractGeometries && this.atgarderPreloadSrc.value.length === 0
                    };
                }
                this.addAtgard(skogsvardAtgard);
            });

        },
        (error) => {
            //
        });
    }

    removeAtgard(guid: string): void {
        const cmd = new RemoveAtgardCommand(guid, this.kontrakt.guid);
        this.kontraktCommandService.update(cmd);
        // Just nu synkas båda listorna (atgard och atgardPreload) efter lite refaktor behövs bara en lista
        // EntityDto[]
        const nextPreloadAtgarder = this.atgarderPreloadSrc.value.filter(x => x.guid !== guid);
        this.atgarderPreloadSrc.next(nextPreloadAtgarder);
        // Atgard[]
        const atgard = this.atgarderSrc.value.find(x => x.guid === guid);
        const nextAtgarder = this.atgarderSrc.value.filter(x => x.guid !== guid);
        this.atgarderSrc.next(nextAtgarder);
        this.atgardRemoved$.next(atgard);
        this.validationService.clearMessagesByEntity("atgard", [
            this.kontrakt.guid,
            guid
        ]);
        // Skogsvard
        this.validationService.clearMessagesByEntity("skogsvardsatgard", [
            this.kontrakt.guid,
            guid
        ]);
    }

    removeRitunderlag(dokumentId: number) {
        const ritunderlag = this.ritunderlagSrc.value.find(x => x.id === dokumentId);
        const nextRitunderlag = this.ritunderlagSrc.value.filter(x => x.id !== dokumentId);
        this.ritunderlagSrc.next(nextRitunderlag);
        this.ritunderlagRemoved$.next(ritunderlag);
    }

    sendChangeAtgardTypCommand(atgardGuid: string, atgardTyp: string): Observable<unknown> {
        if (this.isDirty()) {
            return of(null);
        }
        const command = new ChangeAtgardTypCommand(atgardGuid, atgardTyp);
        return this.sendPristineCommandAndSave(command).pipe(take(1));
    }

    sendResetUtskriftCommand(utskrift: KontraktUtskriftValues): void {
        const command = new UpdateKontraktUtskriftCommand(utskrift.toDto(), this.kontrakt.guid);
        this.sendPristineCommand(command);
    }

    skapaGrotFromKontrakt(rvKontraktGuid: string): void {
        if (this.isDirty() || !this.isGrotuttagforeslaget()) {
            return;
        }
        const command = new CreateGrotkontraktCommand(rvKontraktGuid);
        this.sendPristineCommand(command);
    }

    triggerReloadKontrakt(): Observable<Kontrakt> {
        return this.avtalService.getKontrakt(this.kontrakt.id)
            .pipe(
                take(1),
                tap(kontrakt => {
                    this.kontraktSrc.next(kontrakt);
                })
            );
    }

    updateAtgard(atgard: Atgard): void {
        const atgIndex = this.atgarderSrc.value.findIndex(x => x.guid === atgard.guid);
        if (atgIndex > -1) {
            this.atgarder.splice(atgIndex, 1);
        }
        this.atgarder.push(atgard);
        this.atgarderSrc.next(this.atgarder);
    }

    updateGrotFromRundvirke(grotKontraktGuid: string): void {
        if (this.isDirty()) {
            return;
        }
        const command = new UpdateGrotkontraktCommand(grotKontraktGuid);
        this.sendPristineCommand(command);
    }

    updateKontrakt(kontrakt: Kontrakt) {
        // Tar bort valideringar som kom med hämtning av kontrakt
        if (kontrakt?.validationMessages) {
            for (const message of kontrakt.validationMessages) {
                this.validationService.updateClientSideValidation(false, message);
            }
        }
        const command = new UpdateKontraktCommand(this.avtalMediatorService.avtal.guid, kontrakt);
        this.kontraktCommandService.update(command);
        this.kontraktUpdateSrc.next(kontrakt);
    }

    updateKontraktAffarsform(kontrakt: Kontrakt) {
        const command = new ChangeKontraktAffarsformCommand(this.avtalMediatorService.avtal.guid, kontrakt);
        this.sendPristineCommand(command);
        this.atgarderSrc.next([]);
    }

    updateKontraktAtgard(a: Atgard) {
        const index = this.atgarder.findIndex(x => x.guid === a.guid);
        if (index > -1) {
            // replace
            this.atgarder.splice(index, 1, a);
        } else {
            // insert
            this.atgarder.push(a);
        }
        this.atgarder.sort((left, right) => left.iDate < right.iDate ? -1 : 1);
        this.atgarderSrc.next(this.atgarder);
    }

    updateKontraktCertifikat(certifikat: KontraktCertifikatEditDTO[]) {
        const kontrakt = this.kontrakt;
        const command = new UpdateKontraktCertifikatCommand(kontrakt.guid, certifikat);
        // rensar eventuella valideringsfel så att användaren kommer förbi backend-regeln CertifikatstypRule om de endast ändrar på certifikaten
        this.validationService.clearMessagesByEntity("certifikat", [this.kontrakt.guid]);
        this.kontraktCommandService.update(command);
    }

    updateKontraktTyp(kontrakt: Kontrakt) {
        const command = new ChangeKontraktTypCommand(this.avtalMediatorService.avtal.guid, kontrakt);
        this.sendPristineCommand(command);
    }

    private getIsReadonly(kontrakt: Kontrakt): boolean {
        return kontrakt.isKontraktMakulerat;
    }

    private reloadKontrakt(id: number) {
        this.avtalService.getKontrakt(id)
            .pipe(take(1))
            .subscribe({
                next: (kontrakt) => {
                    kontrakt.validationMessages?.forEach(message => this.validationService.updateClientSideValidation(true, message));
                    this.kontraktSrc.next(kontrakt);
                    this.readonlySrc.next(this.getIsReadonly(kontrakt));
                    this.eventHubService.unregisterListener(`virkeskopskontrakt/${kontrakt.id}`, true)
                        .then(() => this.eventHubService.registerListener(`virkeskopskontrakt/${kontrakt.id}`, true));

                },
                error: console.error,
                complete: () => this.loading$.next(false)
            });
    }

    private sendPristineCommand<T>(command: BasicCommand<T>) {
        this.sendPristineCommandAndSave(command).subscribe();
    }

    private sendPristineCommandAndSave<T>(command: BasicCommand<T>): Observable<unknown> {
        this.commandManager.clear();
        this.kontraktCommandService.update(command);
        return this.avtalMediatorService.save();
    }
}
